import { ceil } from 'lodash';
import { generateNameSuggestionSuffix } from '@bingads-webui-campaign/audience-campaign-shared';
import { xandrVideoCampaignTypes } from '@bingads-webui-campaign-react/campaign-adgroup-creation';
import { getBudgetRange, MinXandrDailyBudgetRangePerCurrency } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';
import { CampaignSubTypeMapping } from '@bingads-webui-campaign-react/constants';
import { getConvertedCurrencyDealMinimumBid, getAccountExchangeRateFromLocalStorage } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { Devices } from '../steps/ad-group/target/device';
import { Genders } from '../steps/ad-group/target/gender';
import { AgeRanges } from '../steps/ad-group/target/age-range';

export const STEP_PATHS = {
  CAMPAIGN_SETTINGS: 'CAMPAIGN_SETTINGS',
  AD_GROUP: 'AD_GROUP',
  ADS: 'ADS',
};

export const ScenarioName = {
  XandrVideoCampaignCreation: 'XandrVideoCampaignCreation',
  XandrVideoAdGroupCreation: 'XandrVideoAdGroupCreation',
  XandrVideoCampaignSettings: 'XandrVideoCampaignSettings',
  XandrVideoAdGroupSettings: 'XandrVideoAdGroupSettings',
  XandrVideoAdsCreation: 'XandrVideoAdsCreation',
};

export const StepLoadingStatus = {
  loading: 'loading',
  ready: 'ready',
  failed: 'failed',
};

const getDefaultBudget = (currencyCode) => {
  if (MinXandrDailyBudgetRangePerCurrency[currencyCode]) {
    return MinXandrDailyBudgetRangePerCurrency[currencyCode];
  }
  const budget = getBudgetRange(currencyCode);

  return budget.Daily.Default;
};

const getInitialCampaignData = (i18n, currencyCode, { xandrVideoDeal, xandrVideoCampaignType, isDisplayAds, serverContext }) => {
  let biddingSchemeType = (xandrVideoCampaignType === xandrVideoCampaignTypes.xandrVideoWeb || isDisplayAds) ? 'ManualCpm' : 'ManualCpv';
  let dealMinimumBid;

  if (xandrVideoDeal) {
    biddingSchemeType = 'ManualCpm';
    if (serverContext) {
      const exchangeRateMapping = getAccountExchangeRateFromLocalStorage(serverContext);
      dealMinimumBid = getConvertedCurrencyDealMinimumBid(xandrVideoDeal.MinimumBid, xandrVideoDeal.Currency, currencyCode, exchangeRateMapping.val);
    }
  }

  let campaignNamePrefix;
  if (isDisplayAds) {
    campaignNamePrefix = _TL_('Display ads');
  } else {
    campaignNamePrefix = _TL_('Video views campaign');
  }

  return {
    Status: 'Active',
    Name: generateNameSuggestionSuffix(i18n.getString(campaignNamePrefix)),
    Budget: {
      Amount: dealMinimumBid ? ceil(dealMinimumBid * 10, 2) : getDefaultBudget(currencyCode),
      Type: 'DailyStandard',
    },
    Languages: ['All'],
    BiddingScheme: {
      Type: biddingSchemeType,
    },
    Target: xandrVideoDeal && xandrVideoDeal.Id,
    CampaignSubType: isDisplayAds ? CampaignSubTypeMapping.DisplayAds : CampaignSubTypeMapping.BrandAwarenessVideoAds,
    CampaignType: 'Audience',
  };
};

export const getInitialCampaignViewModel = (i18n, currencyCode, options) => ({
  data: getInitialCampaignData(i18n, currencyCode, options),
  isValid: true,
  isDirty: true,
});

export const AddedDeviceBidAdjustment = 0;
export const RemovedDeviceBidAdjustment = -100;

export const AdGroupNegativeReferenceId = -111;

export const getInitialAdGroupData = ({ i18n, deviceTypes, dealMinimumBid }) => ({
  Status: 'Active',
  StartDate: new Date(),
  Name: generateNameSuggestionSuffix(i18n.getString(_TL_('Ad group'))),
  locationTargets: {
    bidMode: false,
    currentLevelSettings: {
      initLocations: [],
      peopleInLocation: true,
      peopleSearchingForLocation: true,
      targetOption: 'everywhere',
    },
    parentLevelSettings: {},
    useParentLevel: false,
  },
  adSchedules: {
    adScheduleUseSearcherTimeZone: undefined,
    dayTimeRanges: [],
    timeZone: undefined,
  },
  deviceTypes,
  initialDeviceTypes: Object.keys(Devices).map(device => ({
    criterion: device,
    bidAdjustment: RemovedDeviceBidAdjustment,
  })),
  ageRanges: Object.keys(AgeRanges),
  initialAgeRanges: [],
  genderTypes: Object.keys(Genders),
  initialGenderTypes: [],
  audiences: [],
  initialAudiences: [],
  FrequencyCapSettings: [{}],
  genreTargets: [],
  initialGenreTargets: [],
  CpmBid: dealMinimumBid,
});

export const getInitialAdGroupViewModel = ({ i18n, deviceTypes, dealMinimumBid }) => ({
  data: getInitialAdGroupData({ i18n, deviceTypes, dealMinimumBid }),
  isValid: false,
  isDirty: false,
});
